import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Text } from '@comicrelief/component-library';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { useCartContext } from '@/services/cart/CartContext';

import StepBack from '@/components/stepBack/StepBack';
import { setAudience, Audience } from './payinSlice';
import { audienceOptions, audienceField, getFormResolver } from './payinAudienceFields';
import {
  AudienceSelectLabel,
  AudienceSelectRadioButton,
  AudienceSelectWrapper,
  AudienceImage,
  PayInAudienceContinueButton,
} from './PayinAudience.style';
import { getCurrencySymbol } from '@/locale';
import { getPageHeaderInitialText, getPageHeaderSupplementaryText } from '@/utils/paymentTextUtils';
import PageHeader from '@/components/pageHeader/PageHeader';
import { initOrderReference } from '@/features/paymentSelect/paymentSelectSlice';
import { GTM_EVENTS, setGtmEventChange, setGtmPreviousUserChoice } from '@/services/gtm/gtmSlice';
import { getCurrentJourney } from '@/services/journeys/utils';

function PayinAudience() {
  const dispatch = useAppDispatch();
  const payinState = useAppSelector(state => state.payin);
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const journeysState = useAppSelector(state => state.journeys);
  const urlOverrides = useAppSelector(state => state.urlOverrides);
  const { cartData, cartId } = useCartContext();

  const journeyNavigate = useJourneyNavigate();
  // At this time, the total number of steps is hard coded, in the same way
  // as we're doing for Single payments in src/router/donateSingleRoutes.tsx
  const payinPaySteps = 5;

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: getFormResolver(),
  });

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = formMethods;

  useEffect(() => {
    if (paymentSelectState?.orderReference) {
      return;
    }
    // We're doing this here because we want to ensure that the orderReference is
    // set before we navigate to the next step. It helps the stale session detection
    // to know there's an active session. Usually this would be set on the payment
    // select page, but Payin doesn't have that step.
    dispatch(initOrderReference());
  }, [paymentSelectState, dispatch]);

  // Update our Redux state, triggered by changes to the only
  // inputs on this page; the AudienceSelectRadioButtons:
  useEffect(() => {
    const watchAudience = watch((formValues, { name = '' }) => {
      dispatch(setAudience(formValues[name] as Audience));
    });
    return () => watchAudience.unsubscribe();
  }, [watch, dispatch]);

  const onSubmit = async () => {
    if (isValid) {
      const { amount, currentMoneybuyPosition } = paymentSelectState;
      const thisJourneyName = getCurrentJourney(journeysState);

      // Trigger the 'Payin Activity Page' GTM event, where any
      // 'previousUserChoice' comparison logic will be carried out:
      if (cartId && cartData) {
        dispatch(setGtmEventChange({ eventType: GTM_EVENTS.PAYIN_ACTIVITY_PAGE, cartId, cartData }));
      }

      // THEN, update our previousUserChoice values to keep it in sync,
      // ensuring any subsequent re-selection will work in the same way:
      dispatch(setGtmPreviousUserChoice({
        previousAmount: amount,
        previousPosition: currentMoneybuyPosition,
        previousJourney: thisJourneyName
      }));

      journeyNavigate('next');
    }
  };

  return (
    <FormProvider {...formMethods}>

      <form onSubmit={event => void handleSubmit(onSubmit)(event)}>
        <PageHeader
          currentStep={1}
          totalSteps={payinPaySteps}
          headerText="who are you paying in for?"
          initialText={getPageHeaderInitialText({ isPayIn: true })}
          supplementaryText={getPageHeaderSupplementaryText({ isPayIn: true, payinState })}
          currencySymbol={getCurrencySymbol()}
        />

        <AudienceSelectWrapper>
          <legend hidden>
            Please pick who you are paying in for
          </legend>

          {Object.entries(audienceOptions).map(
            ([audienceValue, audienceData]) => (
              <div key={audienceValue}>
                <AudienceSelectLabel
                  htmlFor={audienceValue}
                  key={audienceValue}
                  selected={payinState.audience === audienceValue}
                  $errorstate={Object.keys(errors).length > 0}
                  data-test={audienceData.testId}
                >
                  <AudienceSelectRadioButton
                    type="radio"
                    id={audienceValue}
                    value={audienceValue}
                    {...register(audienceField.id)}
                    checked={payinState.audience === audienceValue}
                  />
                  <AudienceImage
                    src={`/assets/images/payin/${audienceValue}.png`}
                    alt={audienceData.label}
                  />
                  {audienceData.label}
                </AudienceSelectLabel>
              </div>
            )
          )}
        </AudienceSelectWrapper>

        { errors?.audience?.message && (
          <div style={{ textAlign: 'center' }}>
            <Text color="red" weight="bold">
              {errors?.audience?.message}
            </Text>
          </div>

        )}

        <div>
          <PayInAudienceContinueButton type="submit" fullWidth data-test="payinAudienceContinue">
            Continue to activities
          </PayInAudienceContinueButton>
          <StepBack data-test="payinAudienceGoBack" isFirstJourneyStep urlOverrides={urlOverrides} />
        </div>
      </form>
    </FormProvider>

  );
}

export default PayinAudience;
